import React, { useRef, useEffect } from 'react';
import { TextAreaProps } from './TextArea.types';
import { useMergedRef } from '@mantine/hooks';

const baseClasses =
  'tw:leading-normal tw:outline-none tw:py-1 tw:px-3 tw:rounded-md tw:w-full' +
  ' tw:disabled:pointer-events-none tw:disabled:bg-sq-light-gray tw:disabled:dark:bg-sq-dark-disabled-gray' +
  ' tw:disabled:cursor-not-allowed tw:p-1 tw:border-solid tw:border tw:text-sm';

const darkTheme =
  'tw:dark:bg-sq-dark-background tw:dark:text-sq-dark-text ' +
  'tw:dark:placeholder-sq-dark-text/30! tw:disabled:dark:text-sq-dark-text/30';
const lightTheme = 'tw:text-sq-text-color tw:placeholder-gray-400! tw:disabled:text-sq-darkish-gray';

const errorClasses = 'tw:border-sq-danger';

const borderColorClasses = [
  'tw:border-sq-disabled-gray',
  'tw:dark:border-sq-dark-disabled-gray',
  'tw:hover:border-sq-theme-dark',
  'tw:dark:hover:border-sq-theme-dark-dark',
  'tw:dark:focus:border-sq-theme-darker',
  'tw:dark:active:border-sq-theme-darker',
  'tw:focus:border-sq-theme-dark',
  'tw:active:border-sq-theme-dark',
].join(' ');

/**
 * TextArea.
 */
export const TextArea = React.forwardRef<HTMLTextAreaElement, TextAreaProps>(
  (
    {
      readonly = false,
      disabled = false,
      onChange,
      onKeyUp,
      onFocus,
      onBlur,
      onKeyDown,
      onSelectionChange,
      id,
      name,
      rows = 3,
      cols = undefined,
      value,
      placeholder,
      showError,
      extraClassNames,
      testId,
      autoFocus = false,
    },
    ref,
  ) => {
    const appliedClasses = `${baseClasses} ${extraClassNames} ${lightTheme} ${darkTheme} ${
      showError ? errorClasses : borderColorClasses
    }`;
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const mergedRef = useMergedRef(ref, textareaRef);

    // Handle selection change events
    useEffect(() => {
      if (!onSelectionChange || !textareaRef.current) {
        return;
      }

      const textarea = textareaRef.current;

      const handleSelectionChange = (e: Event) => {
        onSelectionChange(e);
      };

      // Add event listeners for selection changes
      textarea.addEventListener('selectionchange', handleSelectionChange);

      // Also listen for mouse and keyboard events that change selection
      textarea.addEventListener('mouseup', handleSelectionChange);
      textarea.addEventListener('keyup', handleSelectionChange);

      return () => {
        textarea.removeEventListener('selectionchange', handleSelectionChange);
        textarea.removeEventListener('mouseup', handleSelectionChange);
        textarea.removeEventListener('keyup', handleSelectionChange);
      };
    }, [onSelectionChange]);

    return (
      <textarea
        ref={mergedRef}
        data-testid={testId}
        name={name}
        id={id}
        value={value}
        className={appliedClasses}
        placeholder={placeholder}
        disabled={disabled}
        readOnly={readonly}
        onChange={onChange}
        onFocus={onFocus}
        onBlur={onBlur}
        onKeyDown={onKeyDown}
        onKeyUp={onKeyUp}
        rows={rows}
        cols={cols}
        autoFocus={autoFocus}
      />
    );
  },
);
